import { useCallback, useRef, useState } from "react";
import { startForgot, verifyForgot, resetForgot } from "../../services/password/passwordResetApi";
import { describeMfaError } from "../../services/mfa/mfaErrorMessages";

// Forgot Password on step-up (docs/specs/password-reset-stepup.md): email → code → new password → done.
// node's sealed `flow` is kept in a ref and handed back unchanged; the browser never sees a user id.
const CODE_ERROR = "Enter the 6-digit code.";
const START_AGAIN = "That took too long. Please start again.";

const useForgotPasswordV2 = ({ onDone } = {}) => {
  const [step, setStep] = useState("email");
  const [method, setMethod] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const flowRef = useRef(null);

  // Runs one API call with busy/error handling; `onError` may take over a specific error code.
  const run = useCallback(async (call, onError) => {
    setBusy(true);
    setError(null);
    try {
      await call();
    } catch (e) {
      const described = describeMfaError(e);
      if (!onError || !onError(described)) setError(described.message);
    } finally {
      setBusy(false);
    }
  }, []);

  const start = useCallback((address) => run(async () => {
    const response = await startForgot({ email: address });
    flowRef.current = response.flow;
    setEmail(address);
    setMethod(response.method);
    setStep("code");
  }), [run]);

  const submitEmail = useCallback((raw) => start(String(raw || "").trim()), [start]);

  // Email codes only: a new start mails a new code and gives a new flow. An app code needs no resend.
  const resend = useCallback(() => start(email), [start, email]);

  const submitCode = useCallback(async (code) => {
    if (!/^[0-9]{6}$/.test(String(code || ""))) {
      setError(CODE_ERROR);
      return;
    }
    await run(async () => {
      const response = await verifyForgot({ flow: flowRef.current, code });
      flowRef.current = response.flow;
      setStep("password");
    });
  }, [run]);

  const submitPassword = useCallback((newPassword) => run(async () => {
    await resetForgot({ flow: flowRef.current, newPassword });
    flowRef.current = null;
    setStep("done");
    if (onDone) onDone();
  }, (described) => {
    // The step-up token is single-use and lives 5 minutes; after that only a new start helps.
    if (described.code !== "STEP_UP_REQUIRED") return false;
    flowRef.current = null;
    setStep("email");
    setError(START_AGAIN);
    return true;
  }), [run, onDone]);

  return {
    step, method, email, busy, error, canResend: method === "email",
    submitEmail, resend, submitCode, submitPassword,
  };
};

export default useForgotPasswordV2;
